import React, { useState } from 'react';

import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Button, CircularProgress } from '@mui/material';

import useLensPublication from '~/hooks/apis/reporting/useLensPublication';

import type { LensResponse } from '~/types/leLens';

/** Publish the confirmed revision before making its link available to the reader. */
export default function LensPublishCopyButton({
  detail,
  viewerEmail,
  disabled,
  copied,
  prepare,
  copy,
  onPublished,
  onError,
  onBusy,
}: {
  detail: LensResponse;
  viewerEmail?: string | null;
  disabled: boolean;
  copied: boolean;
  prepare: () => Promise<LensResponse>;
  copy: () => Promise<void>;
  onPublished: (response: LensResponse) => void;
  onError: (message: string) => void;
  onBusy: (busy: boolean) => void;
}) {
  const publication = useLensPublication(detail.lens.id);
  const [phase, setPhase] = useState<'publishing' | 'copying' | null>(null);
  const working = phase !== null;
  const state = detail.publication;
  const locked = Boolean(state?.editorEmail && state.editorEmail.toLowerCase() !== viewerEmail?.toLowerCase());
  const needsPublish =
    state?.publishedVersion === null || Boolean(state?.hasUnpublishedChanges && detail.access.canEdit && !locked);
  const publishAndCopy = async () => {
    setPhase(needsPublish ? 'publishing' : 'copying');
    onBusy(true);
    try {
      const latest = await prepare();
      const latestState = latest.publication;
      if (!latestState) {
        throw new Error('Could not confirm the published version. Reload the Lens and try again.');
      }
      const draftLocked = Boolean(
        latestState?.editorEmail && latestState.editorEmail.toLowerCase() !== viewerEmail?.toLowerCase(),
      );
      // A Copy link click must remain copy-only even if another edit arrives during the refetch.
      const shouldPublish =
        needsPublish && (latestState.publishedVersion === null || latestState.hasUnpublishedChanges);
      if (!shouldPublish && latestState.publishedVersion === null) {
        throw new Error('The draft editor needs to publish this Lens before its link can be shared.');
      }
      if (shouldPublish) {
        if (!latest.access.canEdit || draftLocked) {
          throw new Error('The draft editor needs to publish this Lens before its link can be shared.');
        }
        const response = await publication.mutateAsync({ action: 'publish', baseVersion: latest.lens.version });
        if (response.publication?.publishedVersion == null) {
          throw new Error('Publishing was not confirmed. Reload the Lens before copying its link.');
        }
        onPublished(response);
      }
      setPhase('copying');
      await copy();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Could not publish and copy the link. Try again.');
    } finally {
      setPhase(null);
      onBusy(false);
    }
  };
  return (
    <Button
      disabled={disabled || working || (needsPublish && (!detail.access.canEdit || locked))}
      onClick={() => void publishAndCopy()}
      startIcon={working ? <CircularProgress size={15} color="inherit" /> : <LinkOutlinedIcon />}
      variant="contained"
    >
      {working
        ? phase === 'publishing'
          ? 'Publishing…'
          : 'Copying…'
        : needsPublish
          ? 'Publish & copy link'
          : copied
            ? 'Copied'
            : 'Copy link'}
    </Button>
  );
}
